import React, {useEffect, useState} from 'react';
import {fetchRef} from '../service/refsCache';

// ─── Types (контракт ClothingItemDetailDto.visualProfile, item-attribute-vector FR-5) ──────

export interface OklchData {
    l: number | null;
    c: number | null;
    h: number | null;
}

export interface PaletteEntryData {
    refColorId: string;
    weight: number;
    oklch?: OklchData | null;
}

/** Material-вектор вещи (attribute-vector-v2, FR-5): оси [0,1], null = неизвестно. */
export interface MaterialVectorData {
    smooth: number | null;
    textured: number | null;
    soft: number | null;
    rigid: number | null;
    matte: number | null;
    glossy: number | null;
    light: number | null;
    heavy: number | null;
    drape: number | null;
    transparency: number | null;
}

export interface VisualProfileData {
    formalityScore: number | null;
    patternIntensity: number | null;
    patternScaleScore: number | null;
    volumeScore: number | null;
    structureScore: number | null;
    lengthScore: number | null;
    /* Расширение вектора v2 (attribute-vector-v2): additive-поля, у старых профилей отсутствуют. */
    waistDefinitionScore?: number | null;
    patternContrastScore?: number | null;
    patternCoverageScore?: number | null;
    material?: MaterialVectorData | null;
    detailLevel?: number | null;
    ornamentLevel?: number | null;
    hardwareLevel?: number | null;
    logoIntensity?: number | null;
    distressingLevel?: number | null;
    styleAffinity: Record<string, number> | null;
    styleSchemaVersion: string | null;
    palette: PaletteEntryData[] | null;
    source: string | null;
    confidence: number | null;
    modelVersion: string | null;
    updatedAt: string | null;
}

interface ColorRef {
    id: string;
    code: string;
    nameRu: string;
    oklchL: number | null;
    oklchC: number | null;
    oklchH: number | null;
}

interface Props {
    profile: VisualProfileData | null;
}

// ─── Helpers ───────────────────────────────────────────────────────────────

const SOURCE_LABEL: Record<string, string> = {
    recognized: 'AI-извлечение',
    inferred: 'Выведено из атрибутов',
    manual: 'Вручную',
};

const pct = (value: number): string => `${Math.round(value * 100)}%`;

/** Русские имена осей material-вектора для мини-блока фактуры. */
const MATERIAL_AXIS_LABEL: Record<string, string> = {
    smooth: 'Гладкость',
    textured: 'Фактурность',
    soft: 'Мягкость',
    rigid: 'Жёсткость',
    matte: 'Матовость',
    glossy: 'Блеск',
    light: 'Лёгкость',
    heavy: 'Плотность',
    drape: 'Струящесть',
    transparency: 'Прозрачность',
};

/** Топ-3 выраженных оси фактуры (по значению); остальное — в «Технических данных». */
const topMaterialAxes = (material: MaterialVectorData | null | undefined): Array<[string, number]> => {
    if (!material) return [];
    return Object.entries(material)
        .filter((e): e is [string, number] => e[1] != null && e[1] > 0 && !!MATERIAL_AXIS_LABEL[e[0]])
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3);
};

/** CSS-цвет свотча: фактический оттенок из палитры, иначе эталон справочника, иначе токен-заглушка. */
const swatchColor = (entry: PaletteEntryData, ref: ColorRef | undefined): string | null => {
    const oklch = entry.oklch && entry.oklch.l != null && entry.oklch.c != null
        ? entry.oklch
        : ref && ref.oklchL != null && ref.oklchC != null
            ? {l: ref.oklchL, c: ref.oklchC, h: ref.oklchH}
            : null;
    if (!oklch) return null;
    return `oklch(${oklch.l} ${oklch.c} ${oklch.h ?? 0})`;
};

const Scale: React.FC<{ label: string; value: number }> = ({label, value}) => (
    <div data-testid={`attr-scale-${label}`}>
        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
            <span>{label}</span>
            <span>{pct(value)}</span>
        </div>
        <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
            <div
                className="h-full rounded-full bg-blue-600 dark:bg-blue-500"
                style={{width: pct(Math.min(Math.max(value, 0), 1))}}
            />
        </div>
    </div>
);

/**
 * Блок «Атрибуты сочетаемости» на AI-табе карточки вещи (item-attribute-vector, FR-5):
 * шкалы formality/volume/structure, чипы палитры с долями, бейдж источника/уверенности.
 * Read-only: вектор пишут только системные пайплайны.
 */
const ItemCompatibilityAttributes: React.FC<Props> = ({profile}) => {
    const [colors, setColors] = useState<Record<string, ColorRef>>({});

    const hasPalette = !!profile?.palette?.length;
    useEffect(() => {
        if (!hasPalette) return;
        let cancelled = false;
        fetchRef<ColorRef[]>('colors')
            .then(list => {
                if (!cancelled) {
                    setColors(Object.fromEntries(list.map(c => [c.id, c])));
                }
            })
            .catch(() => {
                // Справочник недоступен — чипы палитры покажут только доли, без имён.
            });
        return () => {
            cancelled = true;
        };
    }, [hasPalette]);

    const scales: Array<{ label: string; value: number | null | undefined }> = profile
        ? [
            {label: 'Формальность', value: profile.formalityScore},
            {label: 'Объём', value: profile.volumeScore},
            {label: 'Структурность', value: profile.structureScore},
            {label: 'Выраженность талии', value: profile.waistDefinitionScore},
            {label: 'Детализация', value: profile.detailLevel},
        ]
        : [];
    const visibleScales = scales.filter((s): s is { label: string; value: number } => s.value != null);
    const materialAxes = topMaterialAxes(profile?.material);

    return (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">Атрибуты сочетаемости</h3>
                {profile?.source && (
                    <span
                        data-testid="attr-source-badge"
                        className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                    >
                        {SOURCE_LABEL[profile.source] ?? profile.source}
                        {profile.confidence != null && ` · ${pct(profile.confidence)}`}
                    </span>
                )}
            </div>

            {!profile && (
                <p data-testid="attr-empty" className="text-sm text-gray-500 dark:text-gray-400">
                    Атрибуты сочетаемости ещё не рассчитаны. Они появятся после AI-распознавания вещи.
                </p>
            )}

            {profile && (
                <div className="space-y-4">
                    {visibleScales.length > 0 && (
                        <div className="space-y-3">
                            {visibleScales.map(s => (
                                <Scale key={s.label} label={s.label} value={s.value}/>
                            ))}
                        </div>
                    )}

                    {materialAxes.length > 0 && (
                        <div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">Фактура</div>
                            <div className="flex flex-wrap gap-2">
                                {materialAxes.map(([axis, value]) => (
                                    <span
                                        key={axis}
                                        data-testid="attr-material-chip"
                                        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                                    >
                                        {MATERIAL_AXIS_LABEL[axis]} · {pct(value)}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {hasPalette && (
                        <div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">Палитра</div>
                            <div className="flex flex-wrap gap-2">
                                {profile.palette!.map(entry => {
                                    const ref = colors[entry.refColorId];
                                    const swatch = swatchColor(entry, ref);
                                    return (
                                        <span
                                            key={entry.refColorId}
                                            data-testid="attr-palette-chip"
                                            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                                        >
                                            <span
                                                aria-hidden="true"
                                                className="w-3 h-3 rounded-full border border-gray-300 dark:border-gray-600 bg-gray-300 dark:bg-gray-600"
                                                style={swatch ? {backgroundColor: swatch} : undefined}
                                            />
                                            {ref?.nameRu ?? 'Цвет'} · {pct(entry.weight)}
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ItemCompatibilityAttributes;
